'use client';

import React, { useMemo, useState, useEffect, useContext, createContext } from 'react';

import { useAuthContext } from '../auth';
import { useServiceDeskContext } from '.';
import { TENANT_ID } from '../../config-global';
import CognitoService from '../../services/cognito';
import { IMessage } from '../../services/service-desk/message/base/types';
import { NEW_MESSAGE_FOR_STUDENT_SUB } from '../../services/service-desk/message/graphql';

type ServiceDeskMessageContextType = {
    latestMessage: IMessage | undefined;
};

// CONTEXT
export const ServiceDeskMessageContext = createContext({} as ServiceDeskMessageContextType);

// PROVIDER
type Props = {
    children: React.ReactNode;
};

export function ServiceDeskMessageProvider({ children }: Props) {
    const { lastJsonMessage, sendMessage } = useServiceDeskContext();

    const [latestMessage, setLatestMessage] = useState<IMessage | undefined>(undefined);

    const { student } = useAuthContext();

    const tenant = TENANT_ID;

    // Handle Messages
    useEffect(() => {
        const handle = async () => {
            if (!lastJsonMessage) return;
            if (!tenant || !student?.id) return;
            const type = lastJsonMessage?.type;

            // CONNECTION ACKNOWLEDGEMENT
            if (type === "connection_ack") {
                const token = await CognitoService.getToken();
                // NEW MESSAGE
                sendMessage(JSON.stringify({
                    id: `${student.id}-new-message`,
                    type: 'start',
                    payload: {
                        data: JSON.stringify({
                            query: NEW_MESSAGE_FOR_STUDENT_SUB,
                            variables: {
                                tenantId: tenant,
                                contactId: student.id
                            }
                        }),
                        extensions: {
                            authorization: {
                                "Authorization": `Bearer ${token}`
                            }
                        }
                    },
                }));
            };

            // DATA RECEIVED
            if (type === "data") {
                const onNewMessage = lastJsonMessage?.payload?.data?.onNewMessageForStudent as (IMessage | undefined);
                if (onNewMessage) {
                    setLatestMessage(onNewMessage);
                }
            };
        }

        handle();
    }, [lastJsonMessage, tenant, student?.id, sendMessage]);

    const memoizedValue = useMemo(
        () => ({
            latestMessage,
        }),
        [latestMessage]
    );

    return <ServiceDeskMessageContext.Provider value={memoizedValue}>{children}</ServiceDeskMessageContext.Provider>;
}

// HOOK
export const useServiceDeskMessageContext = () => {
    const context = useContext(ServiceDeskMessageContext)
    if (!context)
        throw new Error("useServiceDeskMessageContext context must be use inside ServiceDeskMessageProvider");
    return context;
};

